// QR Kiosk v2 — KV2-3 solve report at /solving/:id (PG-15: fetches by number when
// Redux is empty → skeleton → form → ErrorCallout, never blank). Context strip,
// "What was done?", merged close date+time (bug #7), cost posting { id: eur(978), value }
// (bug #1 — CRITICAL, kills the wrong-currency overwrite), prefill (D7/PG-16), submit-error UX,
// green save. canWrite guard (v1 SEC-REQ-3 parity). All thunks reused unchanged.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";
import {
  createSolvingServiceReport,
  getMachineParts,
  getServiceReport,
  getServiceReports,
} from "../../publicSlice";
import { setValidationSummary } from "../kioskV2Slice";
import { ChangeSolvingServiceReport, ServiceReportFileTypeCode } from "../../models/serviceReportsModels";
import { CurrencyCode } from "../../../common/models/CommonModels";
import { KPage } from "../shared/KPage";
import { GD, Glyph } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { ErrorCallout } from "../shared/ErrorCallout";
import { useSurfaceState } from "../shared/useSurfaceState";
import { ValidationSummary } from "../shared/ValidationSummary";
import { FormSkeleton } from "./ReportSkeletons";
import { REPORT_DATE_FORMAT, findPartTranslationKey } from "./reportUtils";
import { FLabel, FieldError, KTextArea, KMoneyInput } from "./formAtoms";
import { KDateTimeField } from "./KDateTimeField";
import { FileTileGrid } from "./FileTileGrid";
import { SolveContextStrip } from "./SolveContextStrip";
import { useKioskFiles } from "./useKioskFiles";

export function SolveReportV2() {
  const { id } = useParams(); // :id = report NUMBER
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { serviceReport, machineParts } = useAppSelector((s) => s.public);
  const { canWrite } = usePermission(Modules.Machinery);
  const { translate } = useTranslation(["public", "servicing"]);
  const { translate: tCommon } = useTranslation("common");

  // Route-scoped report — the caller's selectServiceReport prefill passes this as a bonus.
  const r = serviceReport && id && serviceReport.number?.toLowerCase() === id.toLowerCase() ? serviceReport : undefined;
  const st = useSurfaceState("serviceReport", id); // hook — before any early return

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangeSolvingServiceReport>();
  const { files, addFiles, removeFile, rejected, dismissRejected } = useKioskFiles(
    ServiceReportFileTypeCode.solvingReportDocument
  );

  const [summaryVisible, setSummaryVisible] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const savePress = usePressed();

  useEffect(() => {
    if (id && !r) dispatch(getServiceReport(id)); // PG-15: hard refresh works
  }, [id, r, dispatch]);
  useEffect(() => {
    dispatch(getMachineParts()); // part join for the strip
  }, [dispatch]);

  // Write guard — v1 SEC-REQ-3 parity (adapted targets).
  useEffect(() => {
    if (!canWrite) navigate(r?.machineNumber ? `/public-v2/machines/${r.machineNumber}` : "/public-v2", { replace: true });
  }, [canWrite, r?.machineNumber, navigate]);

  // Prefill existing cost / solvingDescription / closeDate on re-solve (D7/PG-16).
  useEffect(() => {
    if (r) {
      reset({
        solvingDescription: r.solvingDescription || "",
        closeDate: r.closeDate || undefined,
        cost: r.cost?.value != null ? { id: CurrencyCode.eur, value: r.cost.value } : undefined,
        files: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r?.number, reset]);

  useEffect(
    () => () => {
      dispatch(setValidationSummary(null));
    },
    [dispatch]
  );

  const partName = useMemo(() => {
    const key = findPartTranslationKey(machineParts, r?.affectedPartCategoryId);
    return key ? translate(`servicing:machineParts.parts.${key}`) : undefined;
  }, [machineParts, r?.affectedPartCategoryId, translate]);

  if (!canWrite) return null;

  const onInvalid = (fieldErrors: Record<string, unknown>) => {
    const count = Object.keys(fieldErrors).length;
    setSummaryVisible(true);
    dispatch(setValidationSummary({ count, message: translate("kioskV2ValidationTitle") }));
    summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onValid: SubmitHandler<ChangeSolvingServiceReport> = async (model) => {
    if (!r?.number || !r?.machineNumber) return; // unreachable — form only renders with r
    setSubmitting(true);
    setSubmitError(undefined);
    setSummaryVisible(false);
    dispatch(setValidationSummary(null));
    try {
      model.closeDate = model.closeDate || moment().toDate();
      model.files = files;
      // *** bug #1: eur(978) only — one writer, matches the EUR € label; never the wrong currency ***
      model.cost = { id: CurrencyCode.eur, value: Number(model.cost?.value || 0) };
      const res = await dispatch(createSolvingServiceReport({ model, serviceReportNumber: r.number }));
      if (createSolvingServiceReport.rejected.match(res)) {
        setSubmitError(translate("submitFailed"));
        return;
      }
      await dispatch(getServiceReports({ machineNumber: r.machineNumber, query: { statusCodes: ["InProgress", "Solved"] } }));
      navigate(`/public-v2/service-reports/${r.number}`);
    } catch {
      setSubmitError(translate("submitFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  // KV2-5 BUG-1: skeleton covers idle AND pending (via st, hoisted above); error only
  // when THIS report key failed. NO cached-dimmed stale form (§6.4 — solve on stale = clobber).
  const showSkeleton = !r && !st.failed;
  const showError = !r && st.failed;

  return (
    <KPage
      titleKey="markAsSolved"
      glyph={GD.check}
      machineNumber={r?.machineNumber}
      backNested
      backTo={id ? `/public-v2/service-reports/${id}` : undefined}
    >
      {showSkeleton ? (
        <FormSkeleton />
      ) : showError ? (
        <ErrorCallout
          title={translate("kioskV2ReportLoadFailedTitle")}
          hint={translate("kioskV2DashboardLoadFailedHint")}
          onRetry={() => id && dispatch(getServiceReport(id))}
          retryLabel={translate("kioskV2Retry")}
        />
      ) : r ? (
        <>
          <SolveContextStrip report={r} partName={partName} />
          {summaryVisible && (
            <div ref={summaryRef} style={{ margin: "10px 16px 0" }}>
              <ValidationSummary title={translate("kioskV2ValidationTitle")} hint={translate("kioskV2ValidationHint")} />
            </div>
          )}
          <form
            onSubmit={handleSubmit(onValid, onInvalid)}
            className="kv2-enter"
            style={{ padding: "14px 16px 0", display: "flex", flexDirection: "column", gap: 14 }}
          >
            {/* 1 · What was done? */}
            <div>
              <FLabel>{translate("kioskV2WhatWasDone")}</FLabel>
              <KTextArea h={110} maxLength={2000} placeholder={translate("kioskV2DescribeFix")} {...register("solvingDescription")} />
            </div>

            {/* 2 · Close date + time (bug #7) */}
            <div>
              <Controller
                control={control}
                name="closeDate"
                rules={{
                  validate: (v) => {
                    if (!v) return true;
                    if (moment(v) > moment()) return translate("kioskV2CloseDateFuture");
                    if (r.openDate && moment(v) < moment(r.openDate)) {
                      return translate("kioskV2CloseDateBeforeOpen", { date: moment(r.openDate).format(REPORT_DATE_FORMAT) });
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <KDateTimeField
                    value={field.value}
                    onChange={field.onChange}
                    dateLabel={translate("closeDate")}
                    timeLabel={translate("time")}
                    min={r.openDate}
                    disableFuture
                    error={!!errors.closeDate}
                  />
                )}
              />
              {errors.closeDate && <FieldError>{errors.closeDate.message}</FieldError>}
            </div>

            {/* 3 · Cost* (bug #1 — NO hidden cost.id; id set in submit) */}
            <div>
              <FLabel req>{translate("kioskV2Cost")}</FLabel>
              <KMoneyInput
                error={!!errors.cost?.value}
                {...register("cost.value", {
                  required: tCommon("forms.required"),
                  max: { value: 100000, message: translate("maxValue") },
                  min: { value: 0, message: translate("minValue") },
                })}
              />
              {errors.cost?.value && <FieldError>{errors.cost?.value?.message}</FieldError>}
            </div>

            {/* 4 · Documents & photos */}
            <div>
              <FLabel>{translate("kioskV2DocsPhotos")}</FLabel>
              <FileTileGrid
                files={files}
                onAdd={addFiles}
                onRemove={removeFile}
                rejected={rejected}
                onDismissRejected={dismissRejected}
                variant="row"
                inputId="kv2-solve-files"
              />
            </div>

            {/* 5 · Submit error */}
            {submitError && <ErrorCallout title={submitError} />}

            {/* 6 · Save as solved (disabled ONLY while submitting) */}
            <div style={{ padding: "16px 0 0" }}>
              <button
                type="submit"
                {...savePress.pressHandlers}
                disabled={submitting}
                style={{
                  width: "100%",
                  height: 56,
                  borderRadius: 13,
                  border: "none",
                  background: "color-mix(in oklab, var(--green) 82%, var(--ink))",
                  color: "var(--tier-accent-fg)",
                  fontSize: 16,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 9,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  opacity: submitting ? 0.6 : 1,
                  ...savePress.pressStyle,
                }}
              >
                <Glyph d={GD.check} size={19} />
                {translate("saveAsSolved")}
              </button>
            </div>
          </form>
        </>
      ) : null}
    </KPage>
  );
}

export default SolveReportV2;
